import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Event } from "@/data/events";
import { Calendar, Clock, MapPin, User, ExternalLink, CalendarPlus } from "lucide-react";

import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RsvpButton from "@/components/RsvpButton";
import EventShare from "@/components/EventShare";
import ShareEventDialog from "@/components/ShareEventDialog";
import WeeklySpeakerSection from "@/components/WeeklySpeakerSection";
import { useCurrentMemberId } from "@/hooks/useEventRsvp";
import {
  buildGoogleCalendarUrl,
  buildOutlookWebUrl,
  downloadIcs,
} from "@/lib/calendar";

interface AddToCalendarMenuProps {
  event: Event;
  className?: string;
}

const AddToCalendarMenu = ({ event, className }: AddToCalendarMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" className={className}>
        <CalendarPlus className="w-4 h-4 mr-2" />
        Add to Calendar
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56 bg-popover z-50">
      <DropdownMenuItem asChild>
        <a
          href={buildGoogleCalendarUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Calendar
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => downloadIcs(event)}>
        Apple Calendar (.ics)
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a
          href={buildOutlookWebUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Outlook Web
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => downloadIcs(event)}>
        Download .ics file
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);


const truncateWords = (text: string, wordLimit = 50): string => {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "…";
};

interface EventCardProps {
  event: Event;
  autoOpenDetails?: boolean;
}




const EventCard = ({ event, autoOpenDetails }: EventCardProps) => {
  const { memberId } = useCurrentMemberId();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (autoOpenDetails) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenDetails]);





  const typeColors = {
    weekly: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    monthly: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
    special: "bg-amber-500/10 text-amber-700 dark:text-amber-400"
  };

  const typeLabels = {
    weekly: "Networking Meeting",
    monthly: "Monthly Lunch",
    special: "Special Event"
  };

  // Format date as YYYY-MM-DD in local time (matches DB occurrence_date)
  const occurrenceDate = `${event.date.getFullYear()}-${String(event.date.getMonth() + 1).padStart(2, "0")}-${String(event.date.getDate()).padStart(2, "0")}`;
  const canRsvp = !!event.dbEventId;
  const speakerChapter: "washington" | "union" | null =
    event.type === "weekly" && (event.chapter === "washington" || event.chapter === "union")
      ? event.chapter
      : null;

  return (
    <Card className="h-full flex flex-col hover:shadow-xl transition-all duration-300 overflow-hidden">
      {event.imageUrl && (
        <div className="relative w-full aspect-[16/9] bg-muted">
          <img
            src={event.imageUrl}
            alt={`${event.title} flyer`}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </div>
      )}
      <CardHeader>
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge className={typeColors[event.type]}>
            {typeLabels[event.type]}
          </Badge>
          {event.isRecurring && (
            <Badge variant="outline" className="text-xs">
              Recurring
            </Badge>
          )}
        </div>
        <CardTitle className="text-xl">{event.title}</CardTitle>
        <CardDescription className="space-y-2 mt-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span>{format(event.date, "EEEE, MMMM d, yyyy")}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{event.startTime} - {event.endTime}</span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.locationAddress ? `${event.location}, ${event.locationAddress}` : event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary underline-offset-2 hover:underline"
            >
              <div>{event.location}</div>
              {event.locationAddress && (
                <div className="text-xs text-muted-foreground">{event.locationAddress}</div>
              )}
            </a>
          </div>
          {event.hostedBy && (
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 flex-shrink-0" />
              <span>Hosted by {event.hostedBy}</span>
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <p className="text-sm text-muted-foreground mb-4">
          {event.type === "weekly" ? event.description : truncateWords(event.description, 50)}
        </p>

        {speakerChapter && (
          <WeeklySpeakerSection
            chapter={speakerChapter}
            occurrenceDate={occurrenceDate}
            timeLabel={`${event.startTime} - ${event.endTime}`}
            locationLabel={event.location}
          />
        )}

        <div className="mt-auto">
          {canRsvp && (
            <div className="mb-3 flex items-center justify-between gap-2">
              <RsvpButton
                eventId={event.dbEventId!}
                occurrenceDate={occurrenceDate}
                memberId={memberId}
              />
              <ShareEventDialog event={event} />
            </div>
          )}
          {!canRsvp && (
            <div className="mb-3 flex justify-end">
              <ShareEventDialog event={event} />
            </div>
          )}

          <AddToCalendarMenu event={event} className="w-full mb-2" />

          <Dialog open={open} onOpenChange={setOpen}>
            {!speakerChapter && (
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full">
                  View Details
                </Button>
              </DialogTrigger>
            )}

          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4 mb-2">
                <Badge className={typeColors[event.type]}>
                  {typeLabels[event.type]}
                </Badge>
                {event.isRecurring && (
                  <Badge variant="outline">{event.recurrence}</Badge>
                )}
              </div>
              <DialogTitle className="text-2xl">{event.title}</DialogTitle>
              <DialogDescription className="text-base space-y-3 pt-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" />
                  <span>{format(event.date, "EEEE, MMMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5" />
                  <span>{event.startTime} - {event.endTime}</span>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0" />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.locationAddress ? `${event.location}, ${event.locationAddress}` : event.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary underline-offset-2 hover:underline"
                  >
                    <div className="font-medium text-foreground">{event.location}</div>
                    {event.locationAddress && (
                      <div className="text-sm">{event.locationAddress}</div>
                    )}
                  </a>
                </div>
                {event.hostedBy && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5" />
                    <span>Hosted by {event.hostedBy}</span>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {event.imageUrl && (
                <div className="rounded-lg overflow-hidden border">
                  <img
                    src={event.imageUrl}
                    alt={`${event.title} flyer`}
                    className="w-full max-h-[500px] object-contain"
                  />
                </div>
              )}
              <div>
                <h4 className="font-semibold mb-2">About This Event</h4>
                <p className="text-muted-foreground">{event.description}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <AddToCalendarMenu event={event} className="w-full sm:flex-1" />
                {event.externalLink && (
                  <Button variant="outline" className="w-full sm:flex-1" asChild>
                    <a href={event.externalLink} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit Event Page
                    </a>
                  </Button>
                )}
              </div>

              <div className="pt-2 border-t">
                <EventShare event={event} />
              </div>

              {speakerChapter && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium mb-2">Meeting Speaker</p>
                  <WeeklySpeakerSection
                    chapter={speakerChapter}
                    occurrenceDate={occurrenceDate}
                    compact
                  />
                </div>
              )}

              {canRsvp && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium mb-2">RSVP for this meeting</p>
                  <RsvpButton
                    eventId={event.dbEventId!}
                    occurrenceDate={occurrenceDate}
                    memberId={memberId}
                    size="default"
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </CardContent>

    </Card>
  );
};


export default EventCard;
